import React, { useEffect, useRef } from "react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Lenis from "@studio-freight/lenis"
import * as styles from "./PortfolioProject.module.scss"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Link } from "gatsby"
import { useLink } from "../../hooks"

gsap.registerPlugin(ScrollTrigger)

const PortfolioProject = ({ data }) => {
  const {
    sectionTitle,
    sectionDescription: { sectionDescription },
    cardItems,
    portfolioProjectButton,
    viewMore,
  } = data

  const containerRef = useRef(null)

  useEffect(() => {
    const lenis = new Lenis()
    window.lenis = lenis

    const raf = time => {
      lenis.raf(time)
      requestAnimationFrame(raf)
    }
    requestAnimationFrame(raf)

    const container = containerRef.current
    const cards = container.querySelectorAll(`.${styles.projectCard}`)

    cards.forEach(card => {
      const logo = card.querySelector(`.${styles.logo}`)
      const desc = card.querySelector(`.${styles.description}`)
      const image = card.querySelector(`.${styles.image}`)
      const title = card.querySelector("h3")
      const tags = card.querySelector(`.${styles.tags}`)
      const readmore = card.querySelector(`.${styles.readmore}`)

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: card,
          start: window.innerWidth > 839.9 ? "center center" : "top top",
          end: "+=200%",
          scrub: true,
          pin: true,
          anticipatePin: 1,
        },
      })

      if (logo) {
        const isMobile = window.innerWidth < 768

        tl.fromTo(
          logo,
          { scale: 2 },
          {
            scale: 1,
            top: isMobile ? "4rem" : "1rem",
            left: "calc(100% - 6rem)",
            xPercent: 0,
            yPercent: 0,
            duration: 1,
          },
          0
        )
      }

      if (title) {
        tl.fromTo(
          title,
          { opacity: 0, x: -100 },
          { opacity: 1, x: 0, duration: 1 },
          0.2
        )
      }

      if (desc) {
        tl.fromTo(
          desc,
          { opacity: 0, x: -100 },
          { opacity: 1, x: 0, duration: 1 },
          0.4
        )
      }

      if (tags) {
        tl.fromTo(
          tags,
          { opacity: 0, x: -100 },
          { opacity: 1, x: 0, duration: 1 },
          0.6
        )
      }

      if (readmore) {
        tl.fromTo(
          readmore,
          { opacity: 0, x: -100 },
          { opacity: 1, x: 0, duration: 1 },
          0.6
        )
      }

      if (title || desc || tags || readmore) {
        tl.to(
          [title, desc, tags, readmore].filter(Boolean),
          { opacity: 0, x: -100, duration: 1 },
          "+=0.5"
        )
      }

      if (image) {
        tl.fromTo(
          image,
          { opacity: 0, x: 100 },
          { opacity: 1, x: 0, duration: 1 },
          0.5
        ).to(image, { opacity: 0, x: 100, duration: 1 }, "+=0.5")
      }
    })

    return () => ScrollTrigger.getAll().forEach(t => t.kill())
    delete window.lenis
  }, [])

  return (
    <div className={`container c-padding ${styles.wrapper}`}>
      <div className="text-center">
        <h2 className="global-spacing-bottom">{sectionTitle}</h2>
        <p className="global-spacing-bottom">{sectionDescription}</p>
      </div>
      <div className={styles.projectScrollContainer} ref={containerRef}>
        {cardItems.map((card, id) => {
          const cardLink = useLink(card.slug)

          return (
            <div
              key={id + 1}
              className={styles.projectCard}
              style={{ backgroundColor: card.backgroundColor }}
            >
              <GatsbyImage
                image={getImage(card.logo)}
                alt={card.title}
                className={styles.logo}
              />
              <div className={styles.flexContainer}>
                <div className={styles.content}>
                  <div
                    className={`${styles.contentTop} ${
                      id === 0 ? styles.firstItem : ""
                    }`}
                  >
                    <h3 className="global-spacing-bottom">{card.title}</h3>
                    <p className={styles.description}>
                      {card.description.description}
                    </p>
                  </div>
                  <div className={styles.tags}>
                    {card.tags.map((tag, i) => (
                      <div key={i} className={styles.tag}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="19"
                          height="19"
                          viewBox="0 0 19 19"
                          fill="none"
                        >
                          <path
                            d="M9.21259 0.40216C4.23532 0.551177 0.316798 4.71351 0.465885 9.69308C0.614971 14.6726 4.77549 18.5931 9.75276 18.4441C14.73 18.2951 18.6486 14.1327 18.4995 9.15316C18.3504 4.17359 14.1899 0.253143 9.21259 0.40216ZM13.8982 7.69278L8.94755 12.9514C8.60643 13.3138 8.03837 13.3308 7.67618 12.9895L5.04799 10.513C4.9619 10.432 4.8926 10.3349 4.84408 10.2271C4.79556 10.1193 4.76876 10.003 4.76522 9.88489C4.76169 9.76674 4.78148 9.64905 4.82347 9.53857C4.86545 9.42808 4.92881 9.32697 5.00991 9.24101C5.35103 8.87867 5.91909 8.86166 6.28128 9.20295L8.2501 11.0582L12.5797 6.45916C12.9208 6.09681 13.4889 6.0798 13.8511 6.42109C14.2223 6.76211 14.2393 7.33043 13.8982 7.69278Z"
                            fill="#827C6A"
                          />
                        </svg>
                        {tag.title}
                      </div>
                    ))}
                  </div>
                  <div>
                    {card.slug && (
                      <div className={styles.readmore}>
                        <Link to={cardLink} className="primaryFilled">
                          {card.portfolioButton}
                          
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
                <GatsbyImage
                  className={styles.image}
                  image={getImage(card.thumbnailImage)}
                  alt={card.thumbnailImage.title}
                />
              </div>
            </div>
          )
        })}

       
        <div className={styles.stackWrapper}>
          <div className={`${styles.cardStack} ${styles.back}`}></div>
          <div className={`${styles.middle}`}></div>
         
        </div>
      </div>
    </div>
  )
}

export default PortfolioProject
